import * as z from "zod";

import { emit } from "../services/event-listner";

export async function request<T = unknown>(
  url: string,
  {
    schema,
    method = "GET",
    body,
    headers,
  }: {
    schema?: z.ZodSchema<T>;
    method?: RequestInit["method"];
    body?: unknown;
    headers?: HeadersInit;
  } = {},
): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status >= 200 && response.status < 300) {
    if (schema) {
      return schema.parse(await response.json());
    } else {
      return (await response.text()) as T;
    }
  }

  emit("error", await response.json());
  throw new Error("Request failed");
}

export async function getRequest<T = unknown>(
  url: string,
  schema?: z.ZodSchema<T>,
): Promise<T> {
  return await request(url, { method: "GET", schema });
}

export async function postRequest<T = unknown>(
  url: string,
  body: unknown,
  schema?: z.ZodSchema<T>,
): Promise<T> {
  return await request(url, {
    method: "POST",
    body,
    schema,
  });
}

export async function putRequest<T = unknown>(
  url: string,
  body: unknown,
  schema?: z.ZodSchema<T>,
): Promise<T> {
  return await request(url, {
    method: "PUT",
    body,
    schema,
  });
}

export async function patchRequest<T = unknown>(
  url: string,
  body: RequestInit["body"],
  schema?: z.ZodSchema<T>,
): Promise<T> {
  return await request(url, { method: "PATCH", body, schema });
}

export async function deleteRequest<T = unknown>(
  url: string,
  schema?: z.ZodSchema<T>,
): Promise<T> {
  return await request(url, { method: "DELETE", schema });
}
